import Header5Props from "./Header5Props.interface";
import styles from "./Header5.module.scss";

import { CLOSE_ALL_OVERLAYS, OPEN_MENU } from "layout/redux/overlay.actions";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import combineClasses from "helpers/combine-classes";
import IconViewer from "components/IconViewer/IconViewer";
import Logo from "./components/Logo/Logo";
import MenuOverlay from "./components/MenuOverlay/MenuOverlay";
import React, { FunctionComponent, Suspense, useCallback, useEffect, useRef, useState } from "react";
import SearchOverlay from "./components/SearchOverlay/SearchOverlay";
import setHeaderStyles from "./hooks/setHeaderStyles";
import useClickOutside from "layout/hooks/useClickOutside";
import useSearchOverlay from "./components/SearchOverlay/hooks/useSearchOverlay";
import useWindow from "hooks/useWindow";
import isBrowserContext from "helpers/isBrowserContext";

const Header5: FunctionComponent<Header5Props> = (props) => {
    const { device } = useWindow();


    const headerRef = useRef<HTMLDivElement>(null);
    const [isHeaderTop, setIsHeaderTop] = useState(false);
    const isBrowser = isBrowserContext()

    const moodBannerHeight = useSelector(state => state.moodBannerHeight.moodBannerHeight);
    const { toggleSearchOverlay } = useSearchOverlay();
    const overlay = useSelector(state => state.overlay);
    const dispatch = useDispatch();

    const [shouldBannerBeSticky, setShouldBannerBeSticky] = useState(false)
    const [currentMenu, setCurrentMenu] = useState<any>(undefined);

    // Window Resize Handler
    useEffect(() => {

        if(!isBrowser) return; 

        const onResize = () => {
            setShouldBannerBeSticky((device !== "desktop" || window.innerWidth < 1024));
        }
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [headerRef.current])

    useEffect(() => {
        setIsHeaderTop(shouldBannerBeSticky);
    }, [shouldBannerBeSticky])

    let header: any;
    if (props?.theme?.header?.[0] && props?.theme?.header.length > 0) {
        header = props?.theme?.header[0]?.content;
    }
    const headerStyles = setHeaderStyles(header);

    //check if first block is moodBanner
    const page = useSelector(state => state.page.content);
    let isMoodBanner = false;
    if (page && page.blocks && page.blocks.length > 0) {
        isMoodBanner = page?.blocks?.length > 0 && page?.blocks?.[0]?.content._contentType === "blockMoodBanner";
    }

    const isHeaderTopClass = isHeaderTop ? styles.is_header_top : undefined;
    const isNotMoodBannerClass = !isMoodBanner ? styles.not_mood_banner_first : undefined;
    const moodBannerClass = isMoodBanner ? styles.mood_banner_first : undefined;
    const searchOverlayClass = overlay.currentOverlay === "SEARCH_OVERLAY" ? styles.is_active : undefined;

    // Scroll Handler
    useEffect(() => {
        if(!isBrowser) return;
        const calculateIfHeaderIsTop = () => {
            if (headerRef.current) {
                const top = headerRef.current.getBoundingClientRect().top;
                if (shouldBannerBeSticky) {
                    setIsHeaderTop(true)
                }
                else {
                    top <= 0 && window.scrollY > moodBannerHeight - 100 ? setIsHeaderTop(true) : setIsHeaderTop(false);
                }
            }
        };
        window.addEventListener("scroll", calculateIfHeaderIsTop);
        return () => window.removeEventListener("scroll", calculateIfHeaderIsTop)
        
    }, [headerRef.current]);

    const overlayActiveClass = overlay.currentOverlay === "MENU" ? styles.is_active : undefined;

    const toggleMenu = useCallback((event: any) => {
        if (event) {
            event.stopPropagation();
        }
        if (overlay?.currentOverlay === "MENU") {
            dispatch({
                type: CLOSE_ALL_OVERLAYS
            });
        }
        else {
            dispatch({
                type: OPEN_MENU
            });
        }
    }, [overlay.currentOverlay]);

    const onLinkClicked = useCallback((givenMenu: any) => {
        if (currentMenu?.contentUdi === givenMenu.contentUdi) {
            setCurrentMenu(undefined);
        }
        else {
            setCurrentMenu(givenMenu);
        }
    }, [currentMenu]);

    const anchorElementRef = useClickOutside(() => setCurrentMenu(undefined));

    return (
        <header className={combineClasses(styles.wrapper, moodBannerClass, isNotMoodBannerClass, isHeaderTopClass)} style={{ top: isMoodBanner && !isHeaderTop ? moodBannerHeight : 0, ...headerStyles }} ref={headerRef}>
            <div className={styles.inner}>
                {/* Logo */}
                <Logo isMoodBanner={isMoodBanner} isHeaderTop={isHeaderTop} header={header} />

                {/* Navigation */}
                {props.menu ? (
                    <nav className={styles.navigation}>
                        {props.menu?.map((level1Item: any, index: number) => {
                            const isMenuActiveClass = level1Item.contentUdi === currentMenu?.contentUdi ? styles.is_active : undefined;

                            return (
                                <div key={index} ref={anchorElementRef}>
                                    <Anchor className={combineClasses(styles.link, isMenuActiveClass)} onClick={() => onLinkClicked(level1Item)} to={level1Item.content.link}>
                                        <span className={styles.link_adjustment}>
                                            {level1Item.content.label}
                                        </span>

                                        {level1Item?.content?.subMenu?.[0] ? (
                                            <div className={styles.sub_menu}>
                                                {level1Item?.content?.subMenu.map((level2Item: any, subIndex: number) => (
                                                    <Anchor key={`${index}_${subIndex}`} className={styles.sub_link} to={level2Item.content.link}>
                                                        <span className={styles.link_adjustment}>
                                                            {level2Item.content.label}
                                                        </span>
                                                    </Anchor>
                                                ))}
                                                <div className={styles.menu_arrow_down} />
                                            </div>
                                        ) : null}
                                    </Anchor>
                                </div>
                            );
                        })}
                    </nav>
                ) : null}

                {/* Arrow */}
                {isMoodBanner ? (
                    <motion.div animate={{ opacity: isHeaderTop ? 0 : 1 }} className={styles.arrow_down} />
                ) : null}

                {/* Search Toggle */}
                <div className={combineClasses(styles.toolbar_button, searchOverlayClass)} onClick={toggleSearchOverlay}>
                    <IconViewer name="search" className={styles.search_icon} />
                </div>

                {/* Burger Menu */}
                <button className={combineClasses(styles.menu_toggle_button, overlayActiveClass)} onClick={(event) => toggleMenu(event)}>
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>

            {overlay.currentOverlay === "MENU" ? (
                <Suspense fallback={null}>
                    <MenuOverlay menu={props.menu} headerStyles={headerStyles} />
                </Suspense>
            ) : null}

            {overlay.currentOverlay === "SEARCH_OVERLAY" || overlay.currentOverlay === "CLOSING_SEARCH_OVERLAY" ? (
                <Suspense fallback={null}>
                    <SearchOverlay state={overlay.currentOverlay} headerStyles={headerStyles} />
                </Suspense>
            ) : null}
        </header>
    );
};

export default Header5;